import { useEffect, useRef, useState } from 'react';
import styles from './MailInbox.module.scss';
import SearchInput from '../../../Atoms/Inputs/SearchInput';
import { IconWrapper } from '../../../Atoms/icons/Icon';
import InboxList from './inbox-list/InboxList';
import InboxFilter from './inbox-list/InboxFilter';
import Tooltip from '../../../Atoms/Tooltip/Tooltip';
import { useFetchMailInbox, useSync } from '../../../../Hooks/Mail';
import { toast } from 'react-toastify';
import MailMenu from './inbox-list/MailMenu';
import { Reference } from 'yup';
import AssignModal from '../../UsersAssignment/AssignModal';
import { X } from 'lucide-react';
import Buttons from '../../../Atoms/Buttons/Buttons';

const tableHeader = [
  { title: 'From', key: 'from' },
  { title: 'FromEmail', key: 'fromEmail' },
  { title: 'To', key: 'toList' },
  { title: 'Subject', key: 'subject' },
  { title: 'Preview', key: 'preview' },
];
const visibleColumn = {
  from: true,
  fromEmail: true,
  toList: true,
  subject: true,
  preview: true,
};

const MailInbox = ({
  setSelectedMail,
  setIsNewMail,
  isNewMail,
  selectedMail,
  refresh,
  onDelete,
  setRefresh,
  mailsType,
  setMailsType,
}) => {
  const scrollRef = useRef(null);

  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedSort, setSelectedSort] = useState('newest');
  const [selectedLink, setSelectedLink] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [allMails, setAllMails] = useState([]);
  const [selectedItem, setSelectedItem] = useState([]);
  const [isAssignUser, setIsAssignUser] = useState({
    isAssigned: false,
    assignTitle: 'Bulk Assign',
  });
  const [selectAllTrigger, setSelectAllTrigger] = useState(false);

  const { mutate: Sync, isLoading: isPending } = useSync();

  const {
    data: mailInbox,
    isLoading,
    refetch,
  } = useFetchMailInbox({
    search: search,
    sort: selectedSort,
    filter: selectedFilter,
    pageSize: pageSize,
    folder: mailsType,
    selectedLink: selectedLink,
  });

  const handleSync = () => {
    Sync(
      {},
      {
        onSuccess: () => {
          toast.success('Synced successfully');
          setRefresh(true);
        },
      },
    );
  };

  useEffect(() => {
    if (refresh) refetch();
  }, [refresh]);

  useEffect(() => {
    if (Array.isArray(mailInbox?.data?.items)) {
      if (refresh) {
        setAllMails(mailInbox.data.items);
      } else {
        setAllMails((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newItems = mailInbox.data.items.filter(
            (m) => !existingIds.has(m.id),
          );
          return [...prev, ...newItems];
        });
      }
    }
  }, [mailInbox, refresh, isRefresh]);
  useEffect(() => {
    if (Array.isArray(mailInbox?.data?.items)) {
      setAllMails(mailInbox.data.items); // always replace with latest
    }
  }, [mailInbox]);

  // useEffect(() => {
  //   const container = scrollRef.current;
  //   if (!container) return;
  //   const handleScroll = () => {
  //     const { scrollTop, scrollHeight, clientHeight } = container;

  //     const total = mailInbox?.data?.total ?? 0;
  //     const loaded = allMails?.length ?? 0;

  //     if (loaded >= total || isLoading) return;

  //     if (scrollTop + clientHeight >= scrollHeight - 5) {
  //       setPageSize((prev) => prev + 4);
  //     }
  //   };
  //   container.addEventListener('scroll', handleScroll);
  //   return () => container.removeEventListener('scroll', handleScroll);
  // }, [allMails?.length, mailInbox?.data?.total, isLoading, refresh]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      const total = mailInbox?.data?.total ?? 0;
      const loaded = allMails?.length ?? 0;

      if (loaded >= total) return;

      if (pageSize >= total) return;

      if (isLoading) return;

      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setPageSize((prev) => {
          const next = prev + 4;

          return next > total ? total : next;
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [allMails?.length, mailInbox?.data?.total, isLoading, pageSize, refresh]);

  useEffect(() => {
    setPageSize(10);
    setAllMails([]);
  }, [search, selectedSort, selectedFilter, mailsType]);

  return (
    <>
      <div className={styles['inboxContainer']}>
        <div className={styles['titleContainer']}>
          <MailMenu
            setMailsType={setMailsType}
            exportData={{
              method: 'get',
              api: '/api/Mail/inbox',
              payload: {
                search: search,
                sort: selectedSort,
                filter: selectedFilter,
                pageSize: mailInbox?.data?.total,
                folder: mailsType,
              },
              visibleColumn: visibleColumn,
              tableHeader: tableHeader,
              fileName: 'MailInboxExport',
            }}
            setSelectedItem={setSelectedItem}
            setSelectedMail={setSelectedMail}
          />

          {!isNewMail && (
            <Tooltip content='New Email'>
              <div
                className={styles.plusIcon}
                onClick={() => setIsNewMail(true)}
              >
                <IconWrapper icon='plus' className={styles.icon} />
              </div>
            </Tooltip>
          )}
        </div>
        {selectedItem?.length > 0 ? (
          <div className={styles.bulkContainer}>
            <div className={styles.selectedInfo}>
              {' '}
              <X
                size={18}
                className={styles.closeIcon}
                onClick={() => setSelectedItem([])}
              />
              <span>{selectedItem?.length} selected</span>
            </div>
            <div className={styles.selectAllCont}>
              <span onClick={() => setSelectAllTrigger((prev) => !prev)}>
                Select All
              </span>
              <Buttons
                text='Assign'
                handleOnClick={() =>
                  setIsAssignUser((prev) => ({
                    ...prev,
                    assignTitle: 'Assign User',
                    isAssigned: true,
                  }))
                }
                rounded
                className={styles.assignButton}
              />
            </div>
          </div>
        ) : (
          <div className={styles['searchContainer']}>
            <div>
              <SearchInput setSearch={setSearch} search={search} />
            </div>
            <Tooltip content='Refresh'>
              {' '}
              <div
                className={`${styles.refresh} ${
                  isPending ? styles['refreshing'] : ''
                }`}
                onClick={isPending ? null : () => handleSync()}
              >
                <IconWrapper icon='refresh' />
              </div>
            </Tooltip>

            <div
              className={styles.burgerWrapper}
              onMouseEnter={() => setShowDropdown(true)}
              onMouseLeave={() => setShowDropdown(false)}
            >
              <IconWrapper icon='burger' />
              {showDropdown && (
                <div className={styles.dropdownMenu}>
                  <InboxFilter
                    selectedFilter={selectedFilter}
                    selectedSort={selectedSort}
                    setSelectedFilter={setSelectedFilter}
                    setSelectedSort={setSelectedSort}
                    setSelectedLink={setSelectedLink}
                    selectedLink={selectedLink}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ overflow: 'scroll' }} ref={scrollRef}>
          <InboxList
            setSelectedMail={setSelectedMail}
            selectedMail={selectedMail}
            onDelete={onDelete}
            mailInbox={allMails}
            refetch={refetch}
            setRefresh={setRefresh}
            isLoading={isLoading}
            setSelectedItem={setSelectedItem}
            selected={selectedItem}
            selectAllTrigger={selectAllTrigger}
          />
        </div>
        {isAssignUser.isAssigned && (
          <AssignModal
            handleOnClose={() =>
              setIsAssignUser((prev) => ({ ...prev, isAssigned: false }))
            }
            selected={selectedItem}
            setSelected={setSelectedItem}
            title={isAssignUser.assignTitle}
            refetch={refetch}
          />
        )}
      </div>
    </>
  );
};

export default MailInbox;
